import React, { Suspense, useState } from 'react';
import { CustomTable } from '../components/CustomTable';
import { fiscalDevicesTableHeaders } from '../utils/Enums/TableHeaders';
import { PaginationComponent } from '../components/Pagination';
import { FiscalDeviceDTO, TableComponentProps } from '../models/table-models';
import { useTableData } from '../utils/data-hooks';
import { resourceCache } from 'use-async-resource';
import { Spinner } from 'react-bootstrap';
import { AddActionComponent } from '../components/AddActionComponent';
import { useTranslation } from 'react-i18next';
import { NoDataComponent } from '../components/NoDataComponent';
import ErrorBoundary from '../components/ErrorBoundary';
import { ErrorComponent } from '../components/ErrorComponent';
import { Role } from '../utils/Enums/Roles';
import { AddEditFiscalDevice } from './AddEditFiscalDevice';
import { useDeleteFiscalDevice, useGetAllFiscalDevices } from '../utils/users-hooks';
import { BatteryCell, LastContactCell, NapCell, RefusalCell } from '../components/FiscalDeviceHealth';

const SORTABLE_COLUMNS = ['fiscalBattery', 'napLastSentAt', 'healthUpdatedAt', 'lastRefusalAt'];
// Колоната в таблицата към полето за подредба - FiscalService.SORTABLE_HEALTH_FIELDS в user-service.
// „fiscalBattery“, защото „batteryVoltage“ вече е заглавие на друга таблица с друг текст.
const SORT_FIELD: Record<string, string> = {
    fiscalBattery: 'batteryVoltage',
    napLastSentAt: 'napLastSentAt',
    healthUpdatedAt: 'healthUpdatedAt',
    lastRefusalAt: 'lastRefusalAt',
};
// Първото натискане подрежда така, че апаратите, които искат внимание, са най-горе.
const FIRST_DIRECTION: Record<string, 'ASC' | 'DESC'> = {
    fiscalBattery: 'ASC',
    napLastSentAt: 'ASC',
    healthUpdatedAt: 'DESC',
    lastRefusalAt: 'DESC',
};

export const FiscalDevices = ({ filter, pagination }: TableComponentProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [sortBy, setSortBy] = useState<string>();
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
    const activeFilter = { ...filter, sortBy: sortBy ? SORT_FIELD[sortBy] : undefined, sortDirection: sortBy ? sortDirection : undefined };
    const [getFiscalDevices, fetchNewDevices] = useTableData<FiscalDeviceDTO[]>(useGetAllFiscalDevices, pagination, activeFilter);

    const handleSortChange = (column: string) => {
        pagination.setCurrentPage(1);
        resourceCache(useGetAllFiscalDevices).clear();
        if (sortBy === column) {
            setSortDirection((current) => (current === 'ASC' ? 'DESC' : 'ASC'));
            return;
        }
        setSortBy(column);
        setSortDirection(FIRST_DIRECTION[column] ?? 'ASC');
    };

    const { items, pager } = getFiscalDevices();
    const refetchEntitiesHook = async () => {
        resourceCache(useGetAllFiscalDevices).clear();
        await fetchNewDevices({
            'pager.page': pagination.currentPage,
            'pager.itemsPerPage': pagination.pageSize,
            pagination,
            filter: activeFilter,
        });
    };

    const useActionHook = async (fields: FiscalDeviceDTO) => {
        await useDeleteFiscalDevice(fields?.id);
        await refetchEntitiesHook();
    };

    return (
        <>
            <div className='d-flex w-100 justify-content-between mb-3'>
                <AddActionComponent
                    accessActionRoles={[Role.ADMIN]}
                    title={t('addFiscalDevice')}
                    addComponent={AddEditFiscalDevice}
                    {...{ refetchEntitiesHook }}
                ></AddActionComponent>
            </div>
            <ErrorBoundary fallback={<ErrorComponent errorText={t('clients')} />}>
                <Suspense fallback={<Spinner animation='border' />}>
                    <>
                        {items ? (
                            <>
                                <CustomTable
                                    columns={{
                                        headings: fiscalDevicesTableHeaders,
                                        items: items.map(
                                            ({
                                                 serialNumber,
                                                 user,
                                                 invoiceStartNumber,
                                                 invoiceEndNumber,
                                                 receiptStartNumber,
                                                 receiptEndNumber,
                                                 id,
                                                 batteryVoltage,
                                                 temperature,
                                                 napLastSentAt,
                                                 healthUpdatedAt,
                                                 lastRefusalAt,
                                                 lastRefusalBits,
                                             }) => [
                                                serialNumber,
                                                user ?? '-',
                                                invoiceStartNumber,
                                                invoiceEndNumber,
                                                receiptStartNumber,
                                                receiptEndNumber,
                                                <BatteryCell key='battery' volts={batteryVoltage} temperature={temperature} />,
                                                <NapCell key='nap' value={napLastSentAt} />,
                                                <LastContactCell key='contact' value={healthUpdatedAt} />,
                                                <RefusalCell
                                                    key='refusal'
                                                    id={id}
                                                    value={lastRefusalAt}
                                                    bits={lastRefusalBits}
                                                    volts={batteryVoltage}
                                                    temperature={temperature}
                                                />,
                                            ],
                                        ),
                                    }}
                                    accessEditRoles={[Role.COORDINATOR, Role.DISTRIBUTOR]}
                                    accessActionRoles={[Role.COORDINATOR, Role.DISTRIBUTOR]}
                                    editComponent={AddEditFiscalDevice}
                                    actionHook={useActionHook}
                                    tableData={items}
                                    sortableColumns={SORTABLE_COLUMNS}
                                    onSortChange={handleSortChange}
                                    {...{ sortBy, sortDirection }}
                                    {...{ refetchEntitiesHook }}
                                ></CustomTable>
                                <PaginationComponent recordsCount={pager.totalCount} pagination={pagination} />
                            </>
                        ) : (
                            <NoDataComponent noDataText={t('fiscalDevices')}></NoDataComponent>
                        )}
                    </>
                </Suspense>
            </ErrorBoundary>
        </>
    );
};
